/**
 * ISO 3166-2 subdivision code utilities for US states and Canadian provinces.
 *
 * Provides display-name lookup for state/province abbreviations.
 * The backend {@link StateCodes} constant must be kept in sync with this file.
 */

/** US state abbreviation → full display name. */
const US_STATES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
  AS: 'American Samoa',
  GU: 'Guam',
  MP: 'Northern Mariana Islands',
  PR: 'Puerto Rico',
  VI: 'U.S. Virgin Islands',
};

/** Canadian province/territory abbreviation → full display name. */
const CA_PROVINCES: Record<string, string> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

/**
 * Returns the full display name for a US state or Canadian province code.
 * Falls back to the code itself for unrecognized values or non-US/CA countries.
 *
 * @param code - Abbreviation (e.g. "CO", "ON") or null/undefined.
 * @param countryIsoCode - ISO 3166-1 alpha-2 country code (e.g. "US", "CA").
 */
export const getStateName = (
  code: string | null | undefined,
  countryIsoCode: string | null | undefined,
): string => {
  if (!code) return '';

  const upper = code.trim().toUpperCase();
  const country = countryIsoCode?.trim().toUpperCase();

  if (country === 'US') return US_STATES[upper] ?? code;
  if (country === 'CA') return CA_PROVINCES[upper] ?? code;

  return code;
};
